import { parseBrainLink } from "@m8t-stack/api-contract";
import { appendBrainLoader, liveBrainLoader, renderBrainLoader } from "./brain-loader-block.js";
import { hasA2aSnippet, appendSnippet } from "./a2a-snippet.js";
import type { AgentVersionRecord, AgentDefinition } from "./foundry-agent-get.js";

/**
 * Recompose deployed instructions from a new base body, preserving whatever overlays the live agent carried.
 * The brain loader is re-rendered from `loaderText` for the link in `metadata.brain`, exactly as `m8t brain link`
 * renders it. Without a readable link or a template, the live loader's text is kept (trimmed and re-marked), even
 * when it is itself unfilled: this release's template is never deployed unfilled, and a loader is never dropped.
 */
export function composeInstructions(args: {
  baseBody: string;
  current: AgentVersionRecord;
  loaderText: string | null;
}): string {
  const live = args.current.definition.instructions ?? "";
  let out = args.baseBody;
  const liveLoader = liveBrainLoader(live);
  if (liveLoader !== null) {
    const link = parseBrainLink(args.current.metadata);
    out = appendBrainLoader(out, link && args.loaderText ? renderBrainLoader(args.loaderText, link) : liveLoader);
  }
  if (hasA2aSnippet(live)) out = appendSnippet(out);
  return out;
}

export function resolveFillableValues(
  current: AgentVersionRecord,
  yamlValues: Record<string, string> | null,
): Record<string, string> | null {
  const raw = current.metadata?.fillableFieldValues;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as Record<string, string>;
    } catch {
      /* fall through */
    }
  }
  return yamlValues;
}

/** True when re-versioning would produce an identical agent (skip createVersion). */
export function isNoop(
  candidate: { definition: AgentDefinition; metadata: Record<string, string> },
  current: AgentVersionRecord,
): boolean {
  const a = candidate.definition.instructions ?? "";
  const b = current.definition.instructions ?? "";
  if (a !== b) return false;
  if (JSON.stringify(candidate.definition.tools ?? []) !== JSON.stringify(current.definition.tools ?? [])) return false;
  return JSON.stringify(sortObj(candidate.metadata)) === JSON.stringify(sortObj(current.metadata ?? {}));
}

function sortObj(o: Record<string, string>): Record<string, string> {
  return Object.fromEntries(Object.entries(o).sort(([x], [y]) => x.localeCompare(y)));
}
